import axios from 'axios'
import stringify from 'qs-stringify'
import jsonpAdapter from 'axios-jsonp'
import { genSign, isNotDef } from '../utils/util'
import { getStore } from '~/plugins/vuex-store'

import getUrl, { urlMap } from '~/constant/apiUrl'
import { sentryReport } from '~/plugins/init-sentry'

let $apiError = () => {}
let $apiSuccess = () => {}

export default function({ $winston, error, store }, inject) {
  if (process.server && $winston) {
    $apiError = (obj) => $winston.apiError.error(`${obj.request_path}`, obj)
    $apiSuccess = (obj) => $winston.apiSuccess.info(`${obj.request_path}`, obj)
  }
  const instance = getServerRequest(customRequest())
  inject('request', function(key, options, extOp = {}) {
    const urlConfig = urlMap[key]
    if (isNotDef(extOp.showNotify)) {
      extOp.showNotify = !!urlConfig.showNotify
    }
    if (isNotDef(extOp.needLogin)) {
      extOp.needLogin = !!urlConfig.needLogin
    }
    if (isNotDef(extOp.errPage)) {
      extOp.errPage = !!urlConfig.errPage
    }
    if (extOp.needLogin && !store.getters.isLogin) {
      store.commit('app/setLoginPop', true)
      return Promise.reject(new Error('need-login'))
    }
    return instance(key, options, extOp).catch((err) => {
      if (extOp.errPage) {
        error(err)
      } else {
        return Promise.reject(err)
      }
    })
  })
  inject('axios', axios)
}

export function getServerRequest(request) {
  return function(urlKey, option = {}, extendsOptions = {}) {
    const store = getStore()
    const { userid, sessionid } = store.state.userInfo
    // 没有登录态的时候不传sessionid
    const userId = sessionid ? userid + '' : ''
    // 如果没有在apiUrl定义特定名称，就用url做key, 后续有接口服务端渲染需要登录态时处理
    const urlConfig = urlMap[urlKey] || { method: 'get', data: {}, headers: {} }
    const url = getUrl(urlKey)

    option.method = option.method || urlConfig.method || 'get'
    option.body = {
      userid: userId,
      user_id: userId,
      sessionid,
      ...urlConfig.data,
      ...(option.body || {})
    }
    option.headers = {
      ...urlConfig.headers,
      ...(option.headers || {})
    }

    const commit = (loading, response = {}, error = false) => {
      store.commit('apiStatus/setApiStatus', {
        key: urlKey,
        value: {
          loading,
          response,
          error,
          isServer: process.server,
          isClient: process.client,
          body: option.body || {}
        }
      })
    }
    commit(true)
    return request(url, option, extendsOptions)
      .then((data) => {
        commit(false, data)
        return data
      })
      .catch((err) => {
        commit(false, err, true)
        return Promise.reject(err)
      })
  }
}

export function jsonp(url, callback = 'callback') {
  return axios({
    url,
    adapter: jsonpAdapter,
    callbackParamName: callback
  })
}

function customErrHandler(err = {}, showNotify = false) {
  if (process.client && err.isAxiosError) {
    const errMsg = err.message || ''
    const isTimeout = errMsg.includes('timeout')
    const isNetError = errMsg.includes('Network Error')
    const message = isTimeout
      ? '接口超时'
      : isNetError
      ? '网络异常'
      : '服务器异常'
    showNotify &&
      $nuxt.$notify({
        title: '接口异常',
        message
      })
  }
  if (process.client) {
    if (err.code === 10017) {
      $nuxt.$store.commit('clearLogin')
    }
    if (err.code === 10001) {
      showNotify &&
        $nuxt.$notify({
          title: '领取异常',
          message: `请<a href="https://wpa1.qq.com/lj2by9sP?_type=wpa&qidian=true" target="_blank" style="color: #0000ee;text-decoration: underline;">联系客服</a>处理`,
          duration: 0
        })
      return
    }
    showNotify &&
      $nuxt.$notify({
        title: '接口异常',
        message: err.message
      })
  }
}
export function customRequest({
  baseURL,
  convertResponse = (res) => [res && res.code === 0, res && res.data],
  timeout = 5000,
  withCredentials = true,
  errHandler = customErrHandler
} = {}) {
  const _instance = axios.create({
    baseURL,
    timeout,
    withCredentials,
    proxy:
      process.env.NODE_ENV === 'development'
        ? {
            host: '10.10.62.18',
            port: 8899
          }
        : false
  })
  _instance.interceptors.request.use(
    function(config) {
      const state = getStore().state
      // Do something before request is sent
      // 线上K8S服务端访问同服务的接口，没走网关导致没有https证书
      if (process.server) {
        if (config.url.includes('.xunlei.com')) {
          if (config.url.includes('http')) {
            config.url = config.url.replace('https://', 'http://')
          } else {
            config.url = 'http:' + config.url
          }
        }
        config.headers.Cookie = state.cookie
        config.headers['User-Agent'] = state.userAgent
      }
      return config
    },
    function(error) {
      // Do something with request error
      return Promise.reject(error)
    }
  )
  // convertResponse === false：表示要自定义转换函数
  if (convertResponse) {
    _instance.interceptors.response.use(
      function(response) {
        const res = response.data
        const [flag, data] = convertResponse(res)
        // null表示空值，undefined表示未赋值，析构的时候null会被跳过
        if (flag) {
          $apiSuccess(getRepInfo(response))
          return data === null ? undefined : data
        }
        // Do something with response data
        // process.winstonLog.info(response.config)
        $apiError(getRepInfo(response))
        return Promise.reject(res)
      },
      function(error) {
        // Do something with response error
        $apiError(getErrInfo(error))
        return Promise.reject(error)
      }
    )
  }
  const fn = (url, options, extendOptions = {}) => {
    const [newUrl, newOptions] = getNewOptions(url, options)
    return _instance({
      url: newUrl,
      ...newOptions
    }).catch((err) => {
      if (extendOptions.errHandler) {
        extendOptions.errHandler(err)
      } else {
        errHandler(err, extendOptions.showNotify)
      }
      sentryReport.error(err)
      return Promise.reject(err)
    })
  }
  fn._instance = _instance
  return fn
}

function getNewOptions(url, options = {}) {
  const newOptions = { method: 'get', body: {}, sign: true, ...options }
  if (newOptions.method) {
    newOptions.method = newOptions.method.toUpperCase()
  }
  const isGetDel = ['GET', 'DELETE'].includes(newOptions.method)
  if (isGetDel) {
    newOptions.body._t = Date.now()
  }
  if (newOptions.sign) {
    newOptions.body.sign = genSign(newOptions.body)
    delete newOptions.sign
  }
  if (isGetDel) {
    newOptions.params = newOptions.body
  } else if (['POST', 'PUT'].includes(newOptions.method)) {
    // 'Content-Type': 'application/x-www-form-urlencoded',
    newOptions.headers = {
      Accept: 'application/json',
      // https://github.com/github/fetch/issues/505
      'Content-Type': 'application/json; charset=utf-8',
      ...newOptions.headers
    }
    if (newOptions.headers['Content-Type'].includes('x-www-form-urlencoded')) {
      newOptions.body = stringify(newOptions.body)
    } else if (newOptions.headers['Content-Type'].includes('json')) {
      newOptions.body = JSON.stringify(newOptions.body)
    }
    newOptions.data = newOptions.body
  }

  delete newOptions.body
  return [url, newOptions]
}

function getRepInfo({ status, data, request, config, headers }) {
  data = data || {}
  return {
    request_date: headers.date,
    request_path: config.url,
    request_cookie: config.headers.Cookie,
    request_ip_addr: headers['x-real-ip'],
    request_ua: config.headers['User-Agent'],
    request_url_param: config.params,
    request_body: config.data,
    response_status: status,
    request_method: config.method,
    return_code: data.code,
    return_message: data.message,
    return_data: data.data,
    trace_id: data.trace_id
  }
}

function getErrInfo({ request, response }) {
  if (!response || !request) return {}
  const { headers, status, statusText, config } = response
  return {
    request_date: headers.date,
    request_path: config.url,
    request_cookie: config.headers.Cookie,
    request_ip_addr: request.connection.remoteAddress,
    request_ua: request['User-Agent'],
    request_url_param: config.params,
    request_body: config.data,
    response_status: status,
    response_statusText: statusText,
    request_method: config.method
  }
}
